/* eslint-disable max-len */
import {
  TextField,
  useTranslate,
  ReferenceField,
  useGetOne,
  LinkToType,
  RaRecord,
  Identifier,
  useGetList,
  useList,
  ListContextProvider
} from "react-admin";
import * as User from "providers/user";
import { useMemo } from "react";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import CustomList from "./CustomList";
import DashboardCustomAppointmentListContent from "./DashboardCustomAppointmentListContent";

export enum FunctionFieldTypes {
  SERVICE = "service",
  ARTIST = "artist"
}

export default function DashboardAppointmentList({
  date,
  timezone
}: {
  date: Dayjs | null;
  timezone: string;
}) {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const [searchParams] = useSearchParams();

  const localStorageAppointmentListParams = localStorage.getItem(
    "RaStore.Appointment.listParams"
  );
  const parsedLocalStorageAppointmentListParams =
    localStorageAppointmentListParams &&
    JSON.parse(localStorageAppointmentListParams);

  const isFilteredByLocation = useMemo(
    () =>
      Boolean(
        (searchParams.get("filter") &&
          JSON.parse(searchParams.get("filter") || "{}").location_id) ||
          parsedLocalStorageAppointmentListParams?.filter?.location_id
      ),
    [searchParams, parsedLocalStorageAppointmentListParams?.filter?.location_id]
  );
  // parsedLocalStorageAppointmentListParams?.filter?.location_id ||
  // should be written in both places,
  // if we write it outside ternary operator it will not trigger
  const locationIds = permissions.isArtist
    ? parsedLocalStorageAppointmentListParams?.filter?.location_id ||
      user?.joined_company?.locations?.map((location: any) => location.id)
    : parsedLocalStorageAppointmentListParams?.filter?.location_id ||
      permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.impersonate_company_id
    ? []
    : user?.company?.locations?.map((location: any) => location.id);

  const canceledFilter = parsedLocalStorageAppointmentListParams?.filter
    ?.cancelled_at || {
    cancelled_at: {
      equals: null
    }
  };

  const baseFilters = date
    ? {
        start_time: {
          gte: date?.tz(timezone, true).startOf("day").toISOString()
        },
        end_time: {
          lte: date?.tz(timezone, true).endOf("day").toISOString()
        },
        is_blocked_time: {
          equals: false
        },
        ...(permissions.isArtist && {
          appointment_services: {
            is_blocked_time: {
              equals: false
            },
            some: {
              artist: {
                is: {
                  user_id: user.id
                }
              }
            }
          }
        }),
        ...canceledFilter
      }
    : {
        ...canceledFilter,
        ...(permissions.isArtist && {
          appointment_services: {
            is_blocked_time: {
              equals: false
            },
            some: {
              artist: {
                is: {
                  user_id: user.id
                }
              }
            }
          }
        })
      };

  const filter = isFilteredByLocation
    ? baseFilters
    : {
        ...baseFilters,
        location_id: { in: locationIds }
      };

  const { data, isLoading } = useGetList("Appointment", {
    // filter: {
    //   is_blocked_time: {
    //     equals: false
    //   }
    // },
    filter: filter,
    sort: { field: "start_time", order: "DESC" },
    pagination: { page: 1, perPage: 100 }
  });
  const stableSortedData = useMemo(() => {
    if (!data) return [];
    // Create a copy to avoid mutating the original
    const copy = [...data];
    copy.sort((a, b) => {
      // 1) Sort by start_time
      const timeA = new Date(a.start_time).getTime();
      const timeB = new Date(b.start_time).getTime();
      if (timeA !== timeB) {
        // If they're different, sort normally by start_time
        return timeB - timeA; // if you want DESC, otherwise timeA - timeB for ASC
      }
      // 2) If start_time is the same, sort by id
      return a.id.localeCompare(b.id);
    });
    return copy;
  }, [data]);

  // Then pass stableSortedData to your list context or your custom component
  const listContext = useList({ data: stableSortedData, isLoading });

  return (
    <div className="w-full h-full overflow-hidden">
      <CustomList
        resource="Appointment"
        filter={filter}
        title={translate("pagenames.appointment")}
        hasCreate={false}
        empty={false}
        // sort={{ field: "start_time", order: "DESC" }}
        dashboardFullHeightAndWidth
        // actions={
        //   <CustomAppointmentListActions
        //     title={translate("pagenames.appointments_list")}
        //     dateSelector={
        //       <AppointmentDatesSelectButton
        //         dateValue={dateValue}
        //         setDateValue={setDateValue}
        //         handleDatePopoverClick={handleDatePopoverClick}
        //       />
        //     }
        //   />
        // }
        actions={<div />}
        pagination={<div />}
      >
        <ListContextProvider value={listContext}>
          <DashboardCustomAppointmentListContent
            date={date}
            timezone={timezone}
          />
        </ListContextProvider>
      </CustomList>
    </div>
  );
}

export const AppointmentArtistField = ({
  appointment_service_id,
  type,
  link = false
}: {
  appointment_service_id: string;
  type: FunctionFieldTypes;
  link?: LinkToType<RaRecord<Identifier>> | undefined;
}) => {
  const { data: appointment_service } = useGetOne("AppointmentService", {
    id: appointment_service_id
  });

  if (!appointment_service) {
    return null;
  }

  if (type === FunctionFieldTypes.SERVICE) {
    return (
      <ReferenceField
        source="service_id"
        reference="Service"
        record={appointment_service}
        key={appointment_service}
        link={link}
      >
        <TextField source="name" />
      </ReferenceField>
    );
  }

  return (
    <ReferenceField
      source="artist_id"
      reference="Artist"
      record={appointment_service}
      key={appointment_service}
      link={link}
    >
      <TextField source="user.first_name" />{" "}
      <TextField source="user.last_name" />
    </ReferenceField>
  );
};
